import { useEffect, useRef } from "react";

import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { CheckCircle2, Lock, Mail, Shield } from "lucide-react";

import useTimer from "../../hooks/useTimer";
import bbbBadge from "../../../assets/new_ui_images/bbb-badge.webp";
import { Button } from "../../common/customDesignedComponents/button";
import soc2Badge from "../../../assets/new_ui_images/soc2-type2-badge.png";
import HubspotCookieBreakerInput from "../../common/HubspotCookieBreakerInput";
import { TAILWIND_GRADIENT_SUBMIT_BUTTON_CLASS } from "../../helpers/constants";
import oneMillionBadge from "../../../assets/new_ui_images/one-million-usd.webp";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "../../common/customDesignedComponents/inputOTP";

const benefits = [
  "Verify your identity with a one-time code",
  "Prevent fraudulent signups using fake emails",
  "Send important alerts if there is an issue with your property",
  "Deliver monthly status updates on your property monitoring",
  "Protect your privacy — used only for EquityProtect communications, never shared",
];

// Shown instead when a real estate agent registers through the CAR program.
const carAgentBenefits = [
  {
    title: "Secure Your Professional Account",
    text: "Verify your identity with a one-time security code to protect your account from unauthorized access.",
  },
  {
    title: "Protect Your Clients' Information",
    text: "Help ensure that only authorized agents can access and manage their clients' property protection information.",
  },
  {
    title: "Prevent Unauthorized Agent Registrations",
    text: "Confirm your email address to help prevent fraudulent accounts and protect the integrity of the C.A.R. EquityProtect program.",
  },
  {
    title: "Stay Informed About Your Clients",
    text: "Receive important notifications when clients accept invitations, activate protection, or require your attention.",
  },
  {
    title: "Stay Connected With Your Clients",
    text: "Receive program updates and communications that help you deliver ongoing value to your clients beyond closing.",
  },
];

// Shown instead when an escrow officer registers from a C.A.R. closing invite (card 3853).
const escrowOfficerBenefits = [
  {
    title: "Secure Your Professional Account",
    text: "Verify your identity with a one-time security code to protect your account from unauthorized access.",
  },
  {
    title: "Protect Your Clients' Information",
    text: "Help ensure that only authorized escrow officers can access the transactions assigned to them.",
  },
  {
    title: "Get Notified About New Closings",
    text: "Receive an email when an agent assigns you a closing that includes EquityProtect protection.",
  },
  {
    title: "Stay Informed About Invoices and Payments",
    text: "Receive updates when a closing invoice is created and when payment for the protection is due.",
  },
];

const professionalCopy = {
  agent: {
    heading: "Why We Ask Agents to Verify Their Email",
    intro:
      "Your email is an important part of your EquityProtect professional account. Verifying your email helps us protect your account, securely manage your clients, and ensure you receive important updates about your EquityProtect program.",
    benefits: carAgentBenefits,
  },
  escrowOfficer: {
    heading: "Why We Ask Escrow Officers to Verify Their Email",
    intro:
      "Your email is an important part of your EquityProtect professional account. Verifying your email helps us protect your account, keep your transactions secure, and ensure you receive closing updates from the agents you work with.",
    benefits: escrowOfficerBenefits,
  },
};

const VerifyEmail = ({
  onSubmit,
  handleChangeEmailOTP,
  emailOTPErr,
  emailOTPSucess,
  emailSpamMsg,
  emailAdd,
  emailOTP,
  resendOTPButtonClick,
  tokenVerified,
  goNext,
  isCarAgent = false,
  isEscrowOfficer = false,
}: any) => {
  const hasAdvanced = useRef(false);
  const professional = isEscrowOfficer
    ? professionalCopy.escrowOfficer
    : isCarAgent
      ? professionalCopy.agent
      : null;

  //initializes the timer states for hook
  const { timer, isTimerActive, startTimer, formatTimeForResendOTP } =
    useTimer(120);

  useEffect(() => {
    if (tokenVerified && !hasAdvanced.current) {
      hasAdvanced.current = true;
      goNext();
    }
  }, [tokenVerified, goNext]);

  // Mask email to show only first 2 chars and last part after @
  const maskEmail = (emailAddress: string) => {
    if (!emailAddress || !emailAddress.includes("@")) return "****@****.com";
    const [localPart, domain] = emailAddress.split("@");
    if (!localPart) return `****@${domain || "****.com"}`;

    // Show first 2 and last 2 characters (when possible), mask the middle.
    if (localPart.length <= 4) {
      // Too short to reliably mask without hiding everything; show as-is.
      return `${localPart}@${domain}`;
    }

    const firstTwo = localPart.slice(0, 2);
    const lastTwo = localPart.slice(-2);
    const middleLen = Math.max(localPart.length - 4, 0);
    const middleMask = "*".repeat(middleLen);

    return `${firstTwo}${middleMask}${lastTwo}@${domain}`;
  };

  return (
    <>
      <Helmet>
        <title>
          Verify Email | EquityProtect - Complete Your Account Setup
        </title>
        <meta
          name="description"
          content="Verify your email address to continue with your EquityProtect account setup and start protecting your home equity."
        />
      </Helmet>

      <>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start max-w-6xl mx-auto adbox_wrapper">
          {/* Left Column - Advertisement Box */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:sticky lg:top-32 adbox_width"
          >
            <div className="bg-gradient-teal rounded-2xl p-8 md:p-10 text-white">
              <div className="flex items-center gap-3 mb-6">
                <Shield className="h-10 w-10 text-white" />
                <h2 className="text-2xl md:text-3xl font-display font-medium">
                  Email Validation
                </h2>
              </div>

              <p className="text-lg text-white/90 font-medium mb-2">
                {professional
                  ? professional.heading
                  : "Why We Ask to Verify Your Email"}
              </p>
              <p className="text-white/80 text-sm mb-8">
                {professional
                  ? professional.intro
                  : "Your email helps secure your account and ensures you receive important updates about your property."}
              </p>

              <ul className="space-y-4 mb-8">
                {professional
                  ? professional.benefits.map((benefit, index) => (
                      <motion.li
                        key={benefit.title}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 + index * 0.1 }}
                        className="flex items-start gap-3"
                      >
                        <CheckCircle2 className="h-5 w-5 text-white shrink-0 mt-0.5" />
                        <span>
                          <span className="block font-semibold">
                            {benefit.title}
                          </span>
                          <span className="block text-sm text-white/80">
                            {benefit.text}
                          </span>
                        </span>
                      </motion.li>
                    ))
                  : benefits.map((benefit, index) => (
                      <motion.li
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 + index * 0.1 }}
                        className="flex items-start gap-3"
                      >
                        <CheckCircle2 className="h-5 w-5 text-white shrink-0 mt-0.5" />
                        <span>{benefit}</span>
                      </motion.li>
                    ))}
              </ul>

              <div className="flex items-center gap-4 pt-6 border-t border-white/20">
                <img
                  src={oneMillionBadge}
                  alt="One Million Dollar Guarantee"
                  className="h-16 w-auto"
                />
                <img
                  src={bbbBadge}
                  alt="BBB Accredited Business"
                  className="h-14 w-auto"
                />
                <img
                  src={soc2Badge}
                  alt="SOC 2 Certified"
                  className="h-14 w-auto"
                />
              </div>
            </div>
            {/* Video Section (homeowners only; not shown to agents or escrow officers) */}
            {!professional && (
              <div className="bg-card rounded-2xl border border-border shadow-lg overflow-hidden p-6 mt-6">
                <h3 className="text-lg font-display font-medium text-foreground mb-4 text-center">
                  Need help? Let us guide you through the process
                </h3>
                <div className="rounded-xl overflow-hidden">
                  <iframe
                    width="100%"
                    style={{ aspectRatio: "16/9" }}
                    src="https://www.youtube.com/embed/JFQXhrt_wxY?modestbranding=1&rel=0&showinfo=0"
                    title="EquityProtect Sign Up Guide"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                </div>
              </div>
            )}
          </motion.div>

          {/* Right Column - Email Verification */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="adbox_width"
          >
            {/* Verification Card */}
            <div className="bg-card rounded-2xl border border-border shadow-lg overflow-hidden">
              {/* Secure Connection Bar */}
              <div className="bg-gradient-to-r from-secondary to-cyan-400 py-3 px-4 flex items-center justify-center gap-2">
                <Lock className="h-4 w-4 text-white" />
                <span className="text-white font-medium text-sm">
                  Secure connection
                </span>
              </div>

              {/* Verification Form */}
              <div className="p-8 md:p-10 text-center">
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-secondary/10 flex items-center justify-center">
                    <Mail className="h-8 w-8 text-secondary" />
                  </div>
                </div>

                <h1 className="text-xl md:text-2xl font-display font-medium text-foreground mb-3">
                  Enter your personal token from Email ID
                </h1>
                <p className="text-muted-foreground mb-2">
                  Please enter the 6 digit security code we have sent to your
                  Email ID
                </p>
                <p className="text-foreground font-medium mb-6">
                  {maskEmail(emailAdd)}
                </p>

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (emailOTP.length !== 6) return;
                    onSubmit?.(e);
                  }}
                  id="signup-verify-email-form"
                >
                  {/* Hubspot hidden input for cookie breaker */}
                  <HubspotCookieBreakerInput />

                  {/* OTP Input */}
                  <div className="flex justify-center mb-6">
                    <InputOTP
                      maxLength={6}
                      value={emailOTP}
                      onChange={(value) => handleChangeEmailOTP(value)}
                    >
                      <InputOTPGroup className="gap-2 md:gap-3">
                        <InputOTPSlot
                          index={0}
                          className="w-12 h-14 md:w-14 md:h-16 text-xl border-2 border-border rounded-lg focus:border-secondary focus:ring-secondary"
                        />
                        <InputOTPSlot
                          index={1}
                          className="w-12 h-14 md:w-14 md:h-16 text-xl border-2 border-border rounded-lg focus:border-secondary focus:ring-secondary"
                        />
                        <InputOTPSlot
                          index={2}
                          className="w-12 h-14 md:w-14 md:h-16 text-xl border-2 border-border rounded-lg focus:border-secondary focus:ring-secondary"
                        />
                        <InputOTPSlot
                          index={3}
                          className="w-12 h-14 md:w-14 md:h-16 text-xl border-2 border-border rounded-lg focus:border-secondary focus:ring-secondary"
                        />
                        <InputOTPSlot
                          index={4}
                          className="w-12 h-14 md:w-14 md:h-16 text-xl border-2 border-border rounded-lg focus:border-secondary focus:ring-secondary"
                        />
                        <InputOTPSlot
                          index={5}
                          className="w-12 h-14 md:w-14 md:h-16 text-xl border-2 border-border rounded-lg focus:border-secondary focus:ring-secondary"
                        />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>

                  {emailOTPErr?.length > 0 && emailOTPSucess?.length === 0 ? (
                    <p className="text-danger text-sm mb-6">{emailOTPErr}</p>
                  ) : (
                    <p className="text-secondary text-sm mb-6">
                      {emailOTPSucess}
                    </p>
                  )}
                  {emailSpamMsg?.length > 0 && (
                    <p className="text-secondary text-sm mb-6">
                      <b>{emailSpamMsg?.length > 0 && emailSpamMsg}</b>
                    </p>
                  )}

                  {/* Verify Button */}
                  <Button
                    type="submit"
                    disabled={emailOTP.length !== 6}
                    className={TAILWIND_GRADIENT_SUBMIT_BUTTON_CLASS}
                  >
                    VERIFY EMAIL ID
                  </Button>
                </form>

                {/* Resend Link */}
                {isTimerActive ? (
                  <p className="text-muted-foreground">
                    Resend OTP in
                    <b> {formatTimeForResendOTP(timer)} seconds</b>
                  </p>
                ) : (
                  <p className="text-muted-foreground">
                    Did not receive the code?{" "}
                    <button
                      onClick={() => {
                        resendOTPButtonClick("email");
                        startTimer(120);
                      }}
                      className="text-secondary hover:text-secondary/80 font-medium transition-colors"
                    >
                      Get a new one
                    </button>
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </>
    </>
  );
};

export default VerifyEmail;
